/**
 * DeepSearch Service - Unified Search Pipeline
 * 
 * Phase 7: Cleaned and hardened production version
 * 
 * This service provides a single, unified search API that:
 * - Always uses RPC semantic search as the primary path
 * - Falls back to keyword search only when semantic fails
 * - Normalizes all results to a consistent SearchResult format
 * - Eliminates Edge Function dependencies for search
 * - Provides clean error handling with typed errors
 * 
 * Main API: search(query, options) → SearchResponse
 */

import { supabase } from '../lib/supabase';
import personalizationService from './personalizationService';
import {
  SearchResult,
  SearchResponse,
  SearchOptions,
  Token,
  UserContext,
  EmbeddingVector,
  RPCSearchResult,
  SemanticSearchError,
  KeywordSearchError,
  EmbeddingGenerationError,
  SEARCH_CONSTANTS,
} from '../types/search';
import { 
  expandQueryWithColorFamilies, 
  SYNONYM_TO_FAMILY,
  ALL_COLOR_WORDS,
  getColorFamily
} from '../config/colorMap';

class DeepSearchService {
  private supabaseUrl: string;
  
  // LRU Embedding Cache - avoids re-generating embeddings for common queries
  private embeddingCache: Map<string, { embedding: EmbeddingVector; timestamp: number }> = new Map();
  private readonly EMBEDDING_CACHE_MAX_SIZE = 100;
  private readonly EMBEDDING_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

  constructor() {
    // Security: Only use environment variable, never hardcode URLs
    this.supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!this.supabaseUrl) {
      throw new Error('API configuration missing');
    }
  }
  
  /**
   * Get cached embedding or null if not cached/expired
   */
  private getCachedEmbedding(text: string): EmbeddingVector | null {
    const key = text.toLowerCase().trim();
    const cached = this.embeddingCache.get(key);
    
    if (!cached) return null;
    
    // Check TTL
    if (Date.now() - cached.timestamp > this.EMBEDDING_CACHE_TTL) {
      this.embeddingCache.delete(key);
      return null;
    }
    
    return cached.embedding;
  }
  
  /**
   * Cache an embedding with LRU eviction
   */
  private cacheEmbedding(text: string, embedding: EmbeddingVector): void {
    const key = text.toLowerCase().trim();
    
    // LRU eviction - remove oldest if at capacity
    if (this.embeddingCache.size >= this.EMBEDDING_CACHE_MAX_SIZE) {
      const oldestKey = this.embeddingCache.keys().next().value;
      if (oldestKey) {
        this.embeddingCache.delete(oldestKey);
      }
    }
    
    this.embeddingCache.set(key, { embedding, timestamp: Date.now() });
  }

  // ============================================================================
  // MAIN SEARCH API - Single Entry Point
  // ============================================================================

  /**
   * Unified search method - THE single entry point for all searches
   * 
   * Flow:
   * 1. Extract text query from string or tokens
   * 2. Get user ID from session
   * 3. Try semantic search (RPC) - ALWAYS primary path
   * 4. If semantic fails or returns zero results, fall back to keyword search
   * 5. Return standardized SearchResponse
   * 
   * @param query - Search text or structured tokens
   * @param options - Search options (filters, pagination, etc.)
   * @returns SearchResponse with normalized results
   */
  async search(
    query: string | Token[],
    options: SearchOptions = {}
  ): Promise<SearchResponse> {
    const startTime = Date.now();

    try {
      // 1. Extract text query
      const queryText = this.extractQueryText(query);

      if (!queryText || queryText.trim().length === 0) {
        return this.createEmptyResponse();
      }

      // Expand query for better semantic recall
      const expandedQuery = this.expandQuery(queryText);
      
      // Parse date range from query or options
      const timeRange = options.filters?.timeRange || this.parseTimeTokens(query);

      // 2. Get user ID
      const userId = await this.getUserId();

      // MyMind-Style 4-Level Fallback Hierarchy
      // Level 1: Standard semantic (threshold: 0.30)
      // Level 2: Soft semantic (threshold: 0.15)
      // Level 3: Nearest neighbors (no threshold, top 20)
      // Level 4: Keyword search

      let embeddingError: Error | null = null;

      // LEVEL 1: Standard Semantic Search (threshold: 0.30)
      try {
        console.log('[SEARCH] Level 1: Standard semantic (threshold: 0.30)');
        
        const level1Results = await this.semanticSearchWithThreshold(
          expandedQuery,
          userId,
          query,
          options,
          0.30,
          options.limit || SEARCH_CONSTANTS.DEFAULT_LIMIT
        );

        if (level1Results.length > 0) {
          console.log('[SEARCH] ✓ Level 1 found:', level1Results.length);
          
          // Apply similarity floor filtering
          let filteredResults = this.filterBySimilarityFloor(level1Results, 1);
          
          if (filteredResults && filteredResults.length > 0) {
            // Apply filters
            filteredResults = this.filterByColorMatch(filteredResults, expandedQuery);
            filteredResults = this.filterByPeopleMatch(filteredResults, expandedQuery);
            filteredResults = this.filterByDateRange(filteredResults, timeRange);
            
            if (filteredResults.length > 0) {
              if (typeof window !== 'undefined') {
                window.lastDeepsearchResults = filteredResults.map(r => ({
                  id: r.id,
                  title: r.title,
                  score: r.similarity,
                  source_type: r.source_type,
                }));
              }

              return {
                ok: true,
                results: filteredResults,
                searchMode: 'semantic',
                nextCursor: null,
                meta: {
                  processingTime: Date.now() - startTime,
                  resultCount: filteredResults.length,
                  embeddingUsed: true,
                  userContextUsed: false, // HOTFIX: Personalization disabled
                  fallbackLevel: 1,
                  similarityThreshold: 0.15, // Actual floor applied
                },
              };
            } else {
              console.log('[SEARCH] Level 1 results filtered out by color/people match');
            }
          } else {
            console.log('[SEARCH] Level 1 rejected by similarity floor, trying next level');
          }
        }

        console.log('[SEARCH] Level 1 returned zero');
      } catch (semanticError) {
        console.warn('[SEARCH] Level 1 failed:', semanticError);
        embeddingError = semanticError instanceof Error ? semanticError : new Error('Semantic search failed');
      }

      // LEVEL 2: Soft Semantic Search (threshold: 0.15)
      if (!embeddingError) {
        try {
          console.log('[SEARCH] Level 2: Soft semantic (threshold: 0.15)');
          
          const level2Results = await this.semanticSearchWithThreshold(
            expandedQuery,
            userId,
            query,
            options,
            0.15,
            50
          );

          if (level2Results.length > 0) {
            console.log('[SEARCH] ✓ Level 2 found:', level2Results.length);
            
            // Apply similarity floor filtering
            let filteredResults = this.filterBySimilarityFloor(level2Results, 2);
            
            if (filteredResults && filteredResults.length > 0) {
              // Apply filters
              filteredResults = this.filterByColorMatch(filteredResults, expandedQuery);
              filteredResults = this.filterByPeopleMatch(filteredResults, expandedQuery);
              filteredResults = this.filterByDateRange(filteredResults, timeRange);
              
              if (filteredResults.length > 0) {
                if (typeof window !== 'undefined') {
                  window.lastDeepsearchResults = filteredResults.map(r => ({
                    id: r.id,
                    title: r.title,
                    score: r.similarity,
                    source_type: r.source_type,
                  }));
                }

                return {
                  ok: true,
                  results: filteredResults,
                  searchMode: 'semantic-soft',
                  nextCursor: null,
                  meta: {
                    processingTime: Date.now() - startTime,
                    resultCount: filteredResults.length,
                    embeddingUsed: true,
                    userContextUsed: false, // HOTFIX: Personalization disabled
                    fallbackLevel: 2,
                  similarityThreshold: 0.15,
                },
              };
              } else {
                console.log('[SEARCH] Level 2 results filtered out by color/people match');
              }
            } else {
              console.log('[SEARCH] Level 2 rejected by similarity floor, trying next level');
            }
          }

          console.log('[SEARCH] Level 2 returned zero');
        } catch (semanticError) {
          console.warn('[SEARCH] Level 2 failed:', semanticError);
        }
      }

      // LEVEL 3: Nearest Neighbors (no threshold, top 20)
      if (!embeddingError) {
        try {
          console.log('[SEARCH] Level 3: Nearest neighbors (no threshold, limit: 20)');
          
          const level3Results = await this.semanticSearchWithThreshold(
            expandedQuery,
            userId,
            query,
            options,
            0.0, // No threshold - return nearest neighbors
            20
          );

          if (level3Results.length > 0) {
            console.log('[SEARCH] ✓ Level 3 found:', level3Results.length);
            
            // Apply similarity floor filtering
            let filteredResults = this.filterBySimilarityFloor(level3Results, 3);
            
            if (filteredResults && filteredResults.length > 0) {
              // Apply filters
              filteredResults = this.filterByColorMatch(filteredResults, expandedQuery);
              filteredResults = this.filterByPeopleMatch(filteredResults, expandedQuery);
              filteredResults = this.filterByDateRange(filteredResults, timeRange);
              
              if (filteredResults.length > 0) {
                if (typeof window !== 'undefined') {
                  window.lastDeepsearchResults = filteredResults.map(r => ({
                    id: r.id,
                    title: r.title,
                    score: r.similarity,
                    source_type: r.source_type,
                  }));
                }

                return {
                  ok: true,
                  results: filteredResults,
                  searchMode: 'semantic-nearest',
                  nextCursor: null,
                  meta: {
                    processingTime: Date.now() - startTime,
                    resultCount: filteredResults.length,
                    embeddingUsed: true,
                    userContextUsed: false, // HOTFIX: Personalization disabled
                    fallbackLevel: 3,
                    similarityThreshold: 0.15, // Actual floor applied
                  },
                };
              } else {
                console.log('[SEARCH] Level 3 results filtered out by color/people match');
              }
            } else {
              console.log('[SEARCH] Level 3 rejected by similarity floor, trying keyword fallback');
            }
          }

          console.log('[SEARCH] Level 3 returned zero');
        } catch (semanticError) {
          console.warn('[SEARCH] Level 3 failed:', semanticError);
        }
      }

      // LEVEL 4: Keyword Search (last resort)
      try {
        console.log('[SEARCH] Level 4: Keyword fallback');
        
        const keywordResults = await this.keywordSearch(queryText, userId, options);

        if (keywordResults.length > 0) {
          console.log('[SEARCH] ✓ Level 4 found:', keywordResults.length);
          
          if (typeof window !== 'undefined') {
            window.lastDeepsearchResults = keywordResults;
          }

          return {
            ok: true,
            results: keywordResults,
            searchMode: 'keyword',
            nextCursor: null,
            meta: {
              processingTime: Date.now() - startTime,
              resultCount: keywordResults.length,
              embeddingUsed: false,
              fallbackLevel: 4,
            },
          };
        }

        console.log('[SEARCH] Level 4 returned zero');
      } catch (keywordError) {
        console.error('[SEARCH] Level 4 failed:', keywordError);
      }

      // LEVEL 5: Direct Color Tag Query (for color searches that semantic missed)
      const colorsInQuery = ALL_COLOR_WORDS.filter(color => queryText.toLowerCase().includes(color));
      if (colorsInQuery.length > 0) {
        try {
          console.log('[SEARCH] Level 5: Direct color tag query for:', colorsInQuery);
          
          const colorTagResults = await this.searchByColorTags(colorsInQuery, userId, options);
          
          if (colorTagResults.length > 0) {
            console.log('[SEARCH] ✓ Level 5 found:', colorTagResults.length);
            
            if (typeof window !== 'undefined') {
              window.lastDeepsearchResults = colorTagResults;
            }

            return {
              ok: true,
              results: colorTagResults,
              searchMode: 'color-tag',
              nextCursor: null,
              meta: {
                processingTime: Date.now() - startTime,
                resultCount: colorTagResults.length,
                embeddingUsed: false,
                fallbackLevel: 5,
                colorQuery: colorsInQuery,
              },
            };
          }
          
          console.log('[SEARCH] Level 5 returned zero');
        } catch (colorTagError) {
          console.warn('[SEARCH] Level 5 failed:', colorTagError);
        }
      }

      // LEVEL 6: Instagram Content Search (supplemental social media content)
      if (userId) {
        try {
          console.log('[SEARCH] Level 6: Instagram content keyword search');
          
          const instagramResults = await this.searchInstagramContent(queryText, userId, 20);
          
          if (instagramResults.length > 0) {
            console.log('[SEARCH] ✓ Level 6 found:', instagramResults.length);
            
            // Apply date filter if present
            let filteredResults = this.filterByDateRange(instagramResults, timeRange);
            
            if (filteredResults.length > 0) {
              if (typeof window !== 'undefined') {
                window.lastDeepsearchResults = filteredResults;
              }

              return {
                ok: true,
                results: filteredResults,
                searchMode: 'keyword', // Keyword search on instagram_content
                nextCursor: null,
                meta: {
                  processingTime: Date.now() - startTime,
                  resultCount: filteredResults.length,
                  embeddingUsed: false,
                  fallbackLevel: 6,
                  source: 'instagram_content',
                },
              };
            }
          }
          
          console.log('[SEARCH] Level 6 returned zero');
        } catch (instagramError) {
          console.warn('[SEARCH] Level 6 failed:', instagramError);
        }
      }

      // All levels exhausted - return "none" (rejected by similarity floor)
      console.log('[SEARCH] All levels exhausted, no results found');
      return {
        ok: true,
        results: [],
        searchMode: 'none',
        nextCursor: null,
        meta: {
          processingTime: Date.now() - startTime,
          resultCount: 0,
          embeddingUsed: true,
          fallbackLevel: 0,
          similarityThreshold: 0.15,
        },
      };

    } catch (error) {
      console.error('[SEARCH] Error:', error);
      return {
        ok: false,
        results: [],
        searchMode: 'empty',
        nextCursor: null,
        error: error instanceof Error ? error.message : 'Search failed',
        meta: {
          processingTime: Date.now() - startTime,
          resultCount: 0,
        },
      };
    }
  }

  // ============================================================================
  // PRIVATE SEARCH METHODS
  // ============================================================================

  /**
   * Filter results by minimum similarity floor
   * 
   * Prevents "closest cluster" noise by rejecting results with similarity < 0.15.
   * Returns filtered results or null if all results are below the floor.
   * 
   * @param results - Raw search results
   * @param level - Fallback level for logging
   * @returns Filtered results or null if all rejected
   */
  private filterBySimilarityFloor(
    results: SearchResult[],
    level: number
  ): SearchResult[] | null {
    const SIMILARITY_FLOOR = 0.15;
    
    if (results.length === 0) {
      return results;
    }

    // Check if ALL results are below the floor
    const allBelowFloor = results.every(r => r.similarity < SIMILARITY_FLOOR);
    
    if (allBelowFloor) {
      const maxSim = Math.max(...results.map(r => r.similarity));
      console.log(`[SEARCH] Rejected entire cluster at Level ${level} (all < ${SIMILARITY_FLOOR}, max: ${maxSim.toFixed(3)})`);
      return null;
    }

    // Filter out individual results below the floor
    const filteredResults = results.filter(r => r.similarity >= SIMILARITY_FLOOR);
    
    if (filteredResults.length < results.length) {
      console.log(`[SEARCH] Filtered by similarity floor: ${results.length} → ${filteredResults.length}`);
    }

    // If filtering removed everything, return null
    if (filteredResults.length === 0) {
      console.log(`[SEARCH] All results filtered out at Level ${level} (similarity < ${SIMILARITY_FLOOR})`);
      return null;
    }

    return filteredResults;
  }

  /**
   * Filter results by color if query contains color terms
   * Uses color family mapping for semantic color matching
   * e.g., "burgundy" will match items tagged "red"
   */
  private filterByColorMatch(results: SearchResult[], queryText: string): SearchResult[] {
    // Extract colors from query using the comprehensive color list
    const queryLower = queryText.toLowerCase();
    const colorsInQuery = ALL_COLOR_WORDS.filter(color => queryLower.includes(color));

    if (colorsInQuery.length === 0) {
      // No color in query, return all results
      return results;
    }

    // Expand colors to include their families
    // e.g., if query has "burgundy", we also look for "red"
    const expandedColors: string[] = [];
    colorsInQuery.forEach(color => {
      expandedColors.push(color);
      const family = getColorFamily(color);
      if (family && !expandedColors.includes(family)) {
        expandedColors.push(family);
      }
    });

    console.log(`[SEARCH] Color filter: looking for [${colorsInQuery.join(', ')}] → expanded to [${expandedColors.join(', ')}]`);

    // Filter results to include items with the queried colors OR their family colors
    const filtered = results.filter(item => {
      // Primary check: auto_tags.colors array
      const itemColors = item.auto_tags?.colors || [];
      if (Array.isArray(itemColors) && itemColors.length > 0) {
        const itemColorsLower = itemColors.map((c: string) => c.toLowerCase());
        
        // Check if any item color matches query colors or their families
        const hasColor = expandedColors.some(queryColor => 
          itemColorsLower.some(itemColor => {
            // Direct match
            if (itemColor.includes(queryColor) || queryColor.includes(itemColor)) {
              return true;
            }
            // Family match: check if item color is in same family as query color
            const itemFamily = getColorFamily(itemColor);
            const queryFamily = getColorFamily(queryColor);
            if (itemFamily && queryFamily && itemFamily === queryFamily) {
              return true;
            }
            return false;
          })
        );
        
        if (hasColor) return true;
      }

      // Also check color_family if it exists
      const colorFamily = (item.auto_tags as any)?.color_family;
      if (colorFamily) {
        const familyLower = colorFamily.toLowerCase();
        if (expandedColors.some(c => c === familyLower || getColorFamily(c) === familyLower)) {
          return true;
        }
      }

      // Fallback: Check if item has color in meta.analysis_data.colors.primaryColor
      const primaryColor = ((item as any).meta?.analysis_data?.colors?.primaryColor || '').toLowerCase();
      if (primaryColor) {
        const primaryFamily = getColorFamily(primaryColor);
        if (expandedColors.some(c => primaryColor.includes(c) || c === primaryFamily)) {
          return true;
        }
      }

      // If no color tags exist, don't include it
      return false;
    });

    if (filtered.length < results.length) {
      console.log(`[SEARCH] Color filter: ${results.length} → ${filtered.length} items`);
    }

    // If strict filter removed everything, return original results with a warning
    if (filtered.length === 0) {
      console.log(`[SEARCH] Color filter: No items matched, returning original results`);
      return results;
    }

    return filtered;
  }

  /**
   * Filter results by gender/people if query contains gender terms
   * SOFT FILTER: Only excludes items that explicitly have the WRONG gender
   * Keeps items with no gender info (to avoid filtering out too much)
   */
  private filterByPeopleMatch(results: SearchResult[], queryText: string): SearchResult[] {
    const queryLower = queryText.toLowerCase();
    
    // Check for gender/people terms in query
    const lookingForMan = /\b(man|men|male|guy|gentleman)\b/.test(queryLower);
    const lookingForWoman = /\b(woman|women|female|girl|lady)\b/.test(queryLower);

    if (!lookingForMan && !lookingForWoman) {
      // No gender term in query, return all results
      return results;
    }

    console.log(`[SEARCH] People filter (soft): looking for ${lookingForMan ? 'man/men' : ''}${lookingForMan && lookingForWoman ? ' & ' : ''}${lookingForWoman ? 'woman/women' : ''}`);

    const filtered = results.filter(item => {
      // Check auto_tags.people array
      const itemPeople = item.auto_tags?.people || [];
      const descLower = (item.description || '').toLowerCase();
      const titleLower = (item.title || '').toLowerCase();
      const combinedText = `${titleLower} ${descLower}`;
      
      // Check if item has explicit gender info
      const hasManTag = Array.isArray(itemPeople) && itemPeople.some((p: string) => /\b(man|men|male|guy)\b/i.test(p));
      const hasWomanTag = Array.isArray(itemPeople) && itemPeople.some((p: string) => /\b(woman|women|female|girl|lady)\b/i.test(p));
      const mentionsMan = /\b(man|men|male|guy|he |his )\b/.test(combinedText);
      const mentionsWoman = /\b(woman|women|female|girl|lady|she |her )\b/.test(combinedText);
      
      const hasMan = hasManTag || mentionsMan;
      const hasWoman = hasWomanTag || mentionsWoman;
      
      // If item has NO gender info at all, INCLUDE it (don't filter)
      if (!hasMan && !hasWoman) {
        return true;
      }
      
      // If looking for man only, exclude items that ONLY have woman
      if (lookingForMan && !lookingForWoman) {
        if (hasWoman && !hasMan) {
          return false; // Exclude - only has woman
        }
        return true; // Include if has man OR has no gender info
      }
      
      // If looking for woman only, exclude items that ONLY have man  
      if (lookingForWoman && !lookingForMan) {
        if (hasMan && !hasWoman) {
          return false; // Exclude - only has man
        }
        return true; // Include if has woman OR has no gender info
      }
      
      // Looking for both - return all
      return true;
    });

    if (filtered.length < results.length) {
      console.log(`[SEARCH] People filter: ${results.length} → ${filtered.length} items`);
    }

    return filtered;
  }

  /**
   * Filter results by date range
   * Supports tokens like "this week", "last month", "today", or specific dates
   */
  private filterByDateRange(
    results: SearchResult[], 
    timeRange: { from: Date; to: Date } | undefined
  ): SearchResult[] {
    if (!timeRange?.from && !timeRange?.to) {
      return results;
    }

    console.log('[SEARCH] Applying date filter:', {
      from: timeRange.from?.toISOString(),
      to: timeRange.to?.toISOString()
    });

    const filtered = results.filter(item => {
      const itemDate = new Date(item.created_at);
      
      if (timeRange.from && itemDate < timeRange.from) {
        return false;
      }
      
      if (timeRange.to && itemDate > timeRange.to) {
        return false;
      }
      
      return true;
    });

    if (filtered.length < results.length) {
      console.log(`[SEARCH] Date filter: ${results.length} → ${filtered.length} items`);
    }

    return filtered;
  }

  /**
   * Parse time-related tokens into date range
   * Supports: "today", "yesterday", "this week", "last week", "this month", "last month"
   */
  private parseTimeTokens(query: string | Token[]): { from: Date; to: Date } | undefined {
    const queryText = typeof query === 'string' 
      ? query.toLowerCase() 
      : query.map(t => t.value).join(' ').toLowerCase();
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    if (queryText.includes('today')) {
      return { from: today, to: now };
    }
    
    if (queryText.includes('yesterday')) {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { from: yesterday, to: today };
    }
    
    if (queryText.includes('this week')) {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
      return { from: startOfWeek, to: now };
    }
    
    if (queryText.includes('last week')) {
      const endOfLastWeek = new Date(today);
      endOfLastWeek.setDate(endOfLastWeek.getDate() - endOfLastWeek.getDay());
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
      return { from: startOfLastWeek, to: endOfLastWeek };
    }
    
    if (queryText.includes('this month')) {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: startOfMonth, to: now };
    }
    
    if (queryText.includes('last month')) {
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startOfLastMonth, to: endOfLastMonth };
    }
    
    if (queryText.includes('this year')) {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      return { from: startOfYear, to: now };
    }
    
    return undefined;
  }

  /**
   * Semantic search with custom threshold (private)
   * 
   * MyMind-style fallback helper: Calls the RPC with a specific similarity threshold.
   * Used by the 4-level fallback hierarchy in search().
   * 
   * @param queryText - The text to search for
   * @param userId - User ID for personalization
   * @param originalQuery - Original query for token extraction
   * @param options - Search options
   * @param threshold - Similarity threshold (0.0 = no threshold, 1.0 = exact match)
   * @param limit - Max results to return
   * @throws SemanticSearchError if search fails
   */
  private async semanticSearchWithThreshold(
    queryText: string,
    userId: string | null,
    originalQuery: string | Token[],
    options: SearchOptions,
    threshold: number,
    limit: number
  ): Promise<SearchResult[]> {
    try {
      // 1. Generate query embedding
      const queryEmbedding = await this.generateEmbedding(queryText);

      // Phase 7: Validate embedding before RPC call
      if (!queryEmbedding || !Array.isArray(queryEmbedding)) {
        throw new EmbeddingGenerationError('Embedding generation returned invalid result');
      }

      if (queryEmbedding.length !== SEARCH_CONSTANTS.EMBEDDING_DIMENSIONS) {
        throw new EmbeddingGenerationError(
          `Invalid embedding dimensions: got ${queryEmbedding.length}, expected ${SEARCH_CONSTANTS.EMBEDDING_DIMENSIONS}`
        );
      }

      if (queryEmbedding.some(v => !isFinite(v))) {
        throw new EmbeddingGenerationError('Embedding contains invalid values (NaN or Infinity)');
      }

      // 2. HOTFIX: Personalization disabled - use pure query embedding only
      // Keep user context tracking for future use, but don't influence results
      const finalEmbedding = queryEmbedding;
      const isPersonalized = false; // Hard-coded false - personalization disabled

      console.log('[SEARCH] Personalization: disabled (using pure query embedding)');

      if (userId) {
        // Update context from search (fire and forget) - keep tracking for future
        setTimeout(() => {
          personalizationService.updateContextFromSearch(originalQuery).catch(() => {
            // Silently fail - this is background work
          });
        }, 0);
      }

      // 3. Call RPC with custom threshold
      const { data, error } = await supabase.rpc<RPCSearchResult[]>(
        'deepsearch_semantic_search_custom',
        {
          query_vector: finalEmbedding,
          match_limit: limit,
          p_user_id: userId,
          similarity_threshold: threshold,
        }
      );

      if (error) {
        throw new SemanticSearchError(
          `Semantic search RPC failed: ${error.message}. This usually indicates: (1) embedding service issue, (2) database error, or (3) invalid query parameters. Query: "${queryText.substring(0, 50)}..."`,
          { error, queryText: queryText.substring(0, 100) }
        );
      }

      if (!data || data.length === 0) {
        return [];
      }

      // 4. Normalize results
      const normalizedResults = this.normalizeResults(data);
      
      // HOTFIX: Personalized flag always false (personalization disabled)
      // if (isPersonalized) {
      //   normalizedResults.forEach(result => {
      //     result.personalized = true;
      //   });
      // }
      // Results will have personalized = false by default from normalizeResults

      return normalizedResults;

    } catch (error) {
      if (error instanceof SemanticSearchError || error instanceof EmbeddingGenerationError) {
        throw error;
      }
      throw new SemanticSearchError(
        error instanceof Error ? error.message : 'Semantic search failed',
        { originalError: error, queryText: queryText.substring(0, 100) }
      );
    }
  }

  /**
   * Legacy semantic search method (deprecated, kept for compatibility)
   * @deprecated Use semanticSearchWithThreshold instead
   */
  private async semanticSearch(
    queryText: string,
    userId: string | null,
    originalQuery: string | Token[],
    options: SearchOptions
  ): Promise<SearchResult[]> {
    return this.semanticSearchWithThreshold(
      queryText,
      userId,
      originalQuery,
      options,
      options.minSimilarity || SEARCH_CONSTANTS.DEFAULT_SIMILARITY_THRESHOLD,
      options.limit || SEARCH_CONSTANTS.DEFAULT_LIMIT
    );
  }

  /**
   * Keyword search via RPC (private)
   * 
   * Phase 4: Uses deepsearch.keyword_search SQL RPC.
   * Returns same format as semantic search for consistency.
   * 
   * @throws KeywordSearchError if search fails
   */
  private async keywordSearch(
    queryText: string,
    userId: string | null,
    options: SearchOptions
  ): Promise<SearchResult[]> {
    try {
      if (!userId) {
        return [];
      }

      const { data, error } = await supabase.rpc<RPCSearchResult[]>('keyword_search', {
        query_text: queryText,
        p_user_id: userId,
        match_limit: options.limit || SEARCH_CONSTANTS.DEFAULT_LIMIT,
      });

      if (error) {
        throw new KeywordSearchError(
          `Keyword search RPC failed: ${error.message}. This usually indicates a database error or malformed query. Query: "${queryText.substring(0, 50)}..."`,
          { error, queryText: queryText.substring(0, 100) }
        );
      }

      if (!data || data.length === 0) {
        return [];
      }

      return this.normalizeResults(data);

    } catch (error) {
      if (error instanceof KeywordSearchError) {
        throw error;
      }
      throw new KeywordSearchError(
        error instanceof Error ? error.message : 'Keyword search failed',
        { originalError: error, queryText: queryText.substring(0, 100) }
      );
    }
  }

  /**
   * Search instagram_content for supplemental results
   * Used when semantic search on deepsearch.items may miss social media content
   */
  private async searchInstagramContent(
    queryText: string,
    userId: string,
    limit: number = 20
  ): Promise<SearchResult[]> {
    try {
      // Keyword search on instagram_content
      const { data, error } = await supabase
        .schema('public')
        .from('instagram_content')
        .select('id, title, description, url, analysis_data, tags, created_at, updated_at')
        .eq('user_id', userId)
        .or(`title.ilike.%${queryText}%,description.ilike.%${queryText}%`)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error || !data) {
        return [];
      }

      // Normalize to SearchResult format
      return data.map(item => ({
        id: item.id,
        title: item.title || 'Untitled',
        description: item.description || null,
        url: item.url,
        source_type: 'instagram',
        created_at: item.created_at,
        similarity: 0.5, // Fixed score for keyword matches
        meta: {
          analysis_data: item.analysis_data,
          tags: item.tags,
        },
        auto_tags: {
          topics: item.tags || [],
        },
        raw_text: item.description || null,
        thumbnail_url: item.url,
      })) as SearchResult[];

    } catch (error) {
      console.warn('[SEARCH] Instagram content search error:', error);
      return [];
    }
  }

  /**
   * Direct color tag search - queries items by auto_tags.colors
   * Fallback when semantic search misses color-based queries
   */
  private async searchByColorTags(
    colors: string[],
    userId: string | null,
    options: SearchOptions
  ): Promise<SearchResult[]> {
    try {
      if (!userId) {
        return [];
      }

      // Expand colors to include their families
      const expandedColors: string[] = [...colors];
      colors.forEach(color => {
        const family = getColorFamily(color);
        if (family && !expandedColors.includes(family)) {
          expandedColors.push(family);
        }
      });

      console.log(`[SEARCH] Color tag search: [${colors.join(', ')}] → expanded to [${expandedColors.join(', ')}]`);

      // Query deepsearch.items with JSONB contains for colors
      // We check auto_tags->colors array for any matching color
      const { data, error } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('id, title, description, url, source_type, auto_tags, raw_text, created_at, updated_at, meta')
        .eq('user_id', userId)
        .not('url', 'like', 'pending://%')
        .order('created_at', { ascending: false })
        .limit(options.limit || 50);

      if (error) {
        console.error('[SEARCH] Color tag query error:', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Filter results by color match (JSONB array contains)
      const colorMatchedItems = data.filter(item => {
        const itemColors = item.auto_tags?.colors || [];
        if (!Array.isArray(itemColors) || itemColors.length === 0) return false;
        
        const itemColorsLower = itemColors.map((c: string) => c.toLowerCase());
        return expandedColors.some(queryColor => 
          itemColorsLower.some((itemColor: string) => 
            itemColor.includes(queryColor) || queryColor.includes(itemColor)
          )
        );
      });

      if (colorMatchedItems.length === 0) {
        return [];
      }

      // Map to RPCSearchResult format for normalization
      const rpcFormatResults: RPCSearchResult[] = colorMatchedItems.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description,
        url: item.url,
        source_type: item.source_type,
        similarity: 1.0, // Direct match = high relevance
        summary: null,
        auto_tags: item.auto_tags,
        raw_text: item.raw_text,
        created_at: item.created_at,
        updated_at: item.updated_at,
        metadata: item.meta,
      }));

      return this.normalizeResults(rpcFormatResults);

    } catch (error) {
      console.error('[SEARCH] Color tag search error:', error);
      return [];
    }
  }

  // ============================================================================
  // RESULT NORMALIZATION - Single Source of Truth
  // ============================================================================

  /**
   * Normalize results from RPC to SearchResult format
   * 
   * Phase 7: Removed camelCase fallbacks - RPC always returns snake_case
   */
  private normalizeResults(rawResults: RPCSearchResult[]): SearchResult[] {
    return rawResults.map(item => {
      // Fix thumbnail logic: RPC returns url and metadata, not image_url/thumbnail_url
      // For images: url IS the image
      // For videos: check metadata.thumbnail_url first, then fall back to url
      let thumbnailUrl: string | undefined;
      let imageUrl: string | undefined;
      
      if (item.source_type === 'image') {
        // For images, the url IS both the thumbnail and the image
        thumbnailUrl = item.url || undefined;
        imageUrl = item.url || undefined;
      } else if (item.source_type === 'video') {
        // For videos, try metadata.thumbnail_url first, then url as fallback
        thumbnailUrl = item.metadata?.thumbnail_url || item.metadata?.thumbnail || item.url || undefined;
        imageUrl = thumbnailUrl; // Use same for compatibility
      } else {
        // For other types (pdf, link, etc.), try metadata thumbnails or use url
        thumbnailUrl = item.metadata?.thumbnail_url || item.metadata?.thumbnail || item.url || undefined;
        imageUrl = thumbnailUrl;
      }

      return {
        // Core fields
        id: item.id,
        title: item.title || '',
        description: item.description || null,
        url: item.url || '',
        source_type: item.source_type || 'unknown',
        created_at: item.created_at || new Date().toISOString(),

        // Search-specific fields
        similarity: item.similarity || SEARCH_CONSTANTS.KEYWORD_FALLBACK_SIMILARITY,
        personalized: item.personalized || false,

        // Content fields - Phase 7: Safe defaults for objects
        meta: item.meta && typeof item.meta === 'object' ? item.meta : (item.metadata || {}), // Check both meta and metadata
        auto_tags: item.auto_tags && typeof item.auto_tags === 'object' ? item.auto_tags : {},
        raw_text: item.raw_text || null,

        // Optional fields
        snippet: item.snippet || undefined,
        image_url: imageUrl,  // Fixed: derived from url based on source_type
        thumbnail_url: thumbnailUrl,  // Fixed: derived from url or metadata based on source_type
      };
    });
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Generate embedding for text via deepsearch-auto-embed Edge Function
   * Uses LRU cache to avoid regenerating embeddings for common queries
   * 
   * @throws EmbeddingGenerationError if generation fails
   */
  private async generateEmbedding(text: string): Promise<EmbeddingVector> {
    // Check cache first
    const cached = this.getCachedEmbedding(text);
    if (cached) {
      console.log('[SEARCH] Using cached embedding');
      return cached;
    }
    
    try {
      const headers = await this.getAuthHeaders();
      const url = this.getEdgeFunctionUrl('deepsearch-auto-embed');

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text,
          item_id: crypto.randomUUID(), // Temporary ID for request tracking
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      const { embedding } = await response.json();

      if (!embedding || !Array.isArray(embedding)) {
        throw new Error('Invalid embedding response format');
      }

      if (embedding.length !== SEARCH_CONSTANTS.EMBEDDING_DIMENSIONS) {
        throw new Error(
          `Invalid embedding dimensions: ${embedding.length}, expected ${SEARCH_CONSTANTS.EMBEDDING_DIMENSIONS}`
        );
      }

      // Cache the embedding
      this.cacheEmbedding(text, embedding);
      
      return embedding;

    } catch (error) {
      throw new EmbeddingGenerationError(
        error instanceof Error ? error.message : 'Failed to generate embedding',
        { text: text.substring(0, 100), originalError: error }
      );
    }
  }

  /**
   * Get current user ID from session
   */
  private async getUserId(): Promise<string | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user?.id || null;
    } catch (error) {
      console.warn('[SEARCH] Auth error:', error);
      return null;
    }
  }

  /**
   * Extract plain text query from string or token array
   */
  private extractQueryText(query: string | Token[]): string {
    if (typeof query === 'string') {
      return query.trim();
    }

    if (Array.isArray(query)) {
      // Check if it's structured tokens
      const isStructured = query.length > 0 && typeof query[0] === 'object' && 'type' in query[0];

      if (isStructured) {
        // Extract search terms, including color and tag tokens for semantic search
        return query
          .filter(t => 
            !t.type || 
            t.type === 'term' ||
            t.type === 'color' ||
            t.type === 'tag'
            // Exclude: time, not, channel, collection, type filters
          )
          .map(t => t.value || t)
          .join(' ')
          .trim();
      } else {
        // Plain string array
        return query.join(' ').trim();
      }
    }

    return '';
  }

  /**
   * Create empty search response
   */
  private createEmptyResponse(processingTime: number = 0): SearchResponse {
    return {
      ok: true,
      results: [],
      searchMode: 'empty',
      nextCursor: null,
      meta: {
        processingTime,
        resultCount: 0,
        embeddingUsed: false,
      },
    };
  }

  /**
   * Expand query with related terms for better semantic recall
   */
  private expandQuery(query: string): string {
    const lowerQuery = query.toLowerCase().trim();
    let expandedQuery = query;

    // COLOR FAMILY EXPANSION
    // e.g., "burgundy shirt" -> "burgundy shirt red"
    const colorExpansions = expandQueryWithColorFamilies(query);
    if (colorExpansions.length > 1) {
      // Add the family color terms to the query
      const familyTerms = colorExpansions
        .slice(1) // Skip the original query
        .map(q => {
          // Extract just the family color word that was added
          const words = q.split(' ');
          const origWords = query.toLowerCase().split(' ');
          return words.find(w => !origWords.includes(w.toLowerCase()));
        })
        .filter(Boolean)
        .join(' ');
      
      if (familyTerms) {
        expandedQuery = `${query} ${familyTerms}`;
        console.log(`[SEARCH] Color expansion: "${query}" → "${expandedQuery}"`);
      }
    }

    // Context expansion map
    const expansions: Record<string, string[]> = {
      gym: ['fitness', 'training', 'workout'],
      back: ['spine', 'posture', 'rehab'],
      knees: ['mobility', 'squat', 'joints'],
      legs: ['squat', 'quads', 'lower body'],
      strength: ['power', 'muscle', 'lifting'],
      mobility: ['flexibility', 'range of motion'],
      energy: ['motivation', 'focus', 'productivity'],
      nutrition: ['food', 'diet', 'meal'],
      cardio: ['running', 'endurance', 'aerobic'],
      yoga: ['stretching', 'flexibility', 'mindfulness'],
    };

    // Check if query matches any expansion key
    for (const [key, terms] of Object.entries(expansions)) {
      if (lowerQuery.includes(key)) {
        const relatedTerms = terms.slice(0, 2).join(' ');
        return `${expandedQuery} ${relatedTerms}`;
      }
    }

    return expandedQuery;
  }

  // ============================================================================
  // TYPEAHEAD SUGGESTIONS
  // ============================================================================

  /**
   * Get typeahead suggestions for search autocomplete
   * Returns matching tags, colors, topics, and recent search terms
   */
  async getSuggestions(
    prefix: string,
    options: { limit?: number } = {}
  ): Promise<{ suggestions: Array<{ value: string; type: string; count?: number }> }> {
    const limit = options.limit || 10;
    const prefixLower = prefix.toLowerCase().trim();
    
    if (prefixLower.length < 1) {
      return { suggestions: [] };
    }

    try {
      const userId = await this.getUserId();
      if (!userId) {
        return { suggestions: [] };
      }

      const suggestions: Array<{ value: string; type: string; count?: number }> = [];
      const seen = new Set<string>();

      // 1. Check for color matches
      const matchingColors = ALL_COLOR_WORDS.filter(color => 
        color.startsWith(prefixLower) || color.includes(prefixLower)
      );
      matchingColors.slice(0, 3).forEach(color => {
        if (!seen.has(color)) {
          suggestions.push({ value: color, type: 'color' });
          seen.add(color);
        }
      });

      // 2. Check for time filter matches
      const timeFilters = ['today', 'yesterday', 'this week', 'last week', 'this month', 'last month', 'this year'];
      timeFilters.forEach(tf => {
        if (tf.startsWith(prefixLower) && !seen.has(tf)) {
          suggestions.push({ value: tf, type: 'time' });
          seen.add(tf);
        }
      });

      // 3. Query existing tags from user's items
      const { data: tagsData, error: tagsError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('auto_tags')
        .eq('user_id', userId)
        .not('auto_tags', 'is', null)
        .limit(100);

      if (!tagsError && tagsData) {
        // Extract all topics from auto_tags
        const topicCounts = new Map<string, number>();
        
        tagsData.forEach(item => {
          const topics = item.auto_tags?.topics || [];
          topics.forEach((topic: string) => {
            const topicLower = topic.toLowerCase();
            if (topicLower.includes(prefixLower)) {
              topicCounts.set(topic, (topicCounts.get(topic) || 0) + 1);
            }
          });
        });

        // Sort by count and add to suggestions
        const sortedTopics = Array.from(topicCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);
        
        sortedTopics.forEach(([topic, count]) => {
          if (!seen.has(topic.toLowerCase())) {
            suggestions.push({ value: topic, type: 'tag', count });
            seen.add(topic.toLowerCase());
          }
        });
      }

      // 4. Search for matching titles (limit to 3)
      const { data: titleData, error: titleError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('title')
        .eq('user_id', userId)
        .ilike('title', `%${prefixLower}%`)
        .order('created_at', { ascending: false })
        .limit(5);

      if (!titleError && titleData) {
        titleData.forEach(item => {
          if (item.title && !seen.has(item.title.toLowerCase())) {
            // Extract the matching portion for suggestion
            const titleLower = item.title.toLowerCase();
            if (titleLower.length <= 50) {
              suggestions.push({ value: item.title, type: 'term' });
              seen.add(titleLower);
            }
          }
        });
      }

      console.log('[SEARCH] Suggestions for "' + prefix + '":', suggestions.length);

      return { suggestions: suggestions.slice(0, limit) };

    } catch (error) {
      console.warn('[SEARCH] Suggestions error:', error);
      return { suggestions: [] };
    }
  }

  // ============================================================================
  // VISUAL SIMILARITY SEARCH
  // ============================================================================

  /**
   * Find visually similar items based on an existing item's embedding
   * 
   * @param itemId - ID of the source item to find similar items for
   * @param options - Search options
   * @returns SearchResponse with similar items
   */
  async searchSimilar(
    itemId: string,
    options: { limit?: number; excludeSelf?: boolean } = {}
  ): Promise<SearchResponse> {
    const startTime = Date.now();
    const limit = options.limit || 20;
    const excludeSelf = options.excludeSelf !== false;

    try {
      const userId = await this.getUserId();
      if (!userId) {
        return {
          ok: false,
          results: [],
          searchMode: 'empty',
          nextCursor: null,
          error: 'User not authenticated',
          meta: { processingTime: Date.now() - startTime },
        };
      }

      // 1. Fetch the source item to get its embedding
      console.log('[SEARCH] Finding similar items for:', itemId);
      
      const { data: sourceItem, error: fetchError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('id, title, embedding')
        .eq('id', itemId)
        .eq('user_id', userId)
        .single();

      if (fetchError || !sourceItem) {
        console.warn('[SEARCH] Could not find source item:', fetchError);
        return {
          ok: false,
          results: [],
          searchMode: 'empty',
          nextCursor: null,
          error: 'Source item not found',
          meta: { processingTime: Date.now() - startTime },
        };
      }

      if (!sourceItem.embedding) {
        console.warn('[SEARCH] Source item has no embedding');
        return {
          ok: false,
          results: [],
          searchMode: 'empty',
          nextCursor: null,
          error: 'Source item has no embedding for similarity search',
          meta: { processingTime: Date.now() - startTime },
        };
      }

      // 2. Use the embedding to find similar items via RPC
      const { data, error } = await supabase.rpc<RPCSearchResult[]>(
        'deepsearch_semantic_search_custom',
        {
          query_vector: sourceItem.embedding,
          match_limit: excludeSelf ? limit + 1 : limit, // Fetch extra if excluding self
          p_user_id: userId,
          similarity_threshold: 0.15, // Lower threshold for visual similarity
        }
      );

      if (error) {
        console.error('[SEARCH] Similarity search RPC error:', error);
        return {
          ok: false,
          results: [],
          searchMode: 'empty',
          nextCursor: null,
          error: error.message,
          meta: { processingTime: Date.now() - startTime },
        };
      }

      if (!data || data.length === 0) {
        return {
          ok: true,
          results: [],
          searchMode: 'similar',
          nextCursor: null,
          meta: {
            processingTime: Date.now() - startTime,
            sourceItemId: itemId,
            sourceItemTitle: sourceItem.title,
          },
        };
      }

      // 3. Filter out the source item if needed and normalize
      let filteredData = data;
      if (excludeSelf) {
        filteredData = data.filter(item => item.id !== itemId);
      }

      const results = this.normalizeResults(filteredData.slice(0, limit));

      console.log('[SEARCH] Found', results.length, 'similar items for:', sourceItem.title);

      return {
        ok: true,
        results,
        searchMode: 'similar',
        nextCursor: null,
        meta: {
          processingTime: Date.now() - startTime,
          resultCount: results.length,
          sourceItemId: itemId,
          sourceItemTitle: sourceItem.title,
        },
      };

    } catch (error) {
      console.error('[SEARCH] Similarity search error:', error);
      return {
        ok: false,
        results: [],
        searchMode: 'empty',
        nextCursor: null,
        error: error instanceof Error ? error.message : 'Similarity search failed',
        meta: { processingTime: Date.now() - startTime },
      };
    }
  }

  // ============================================================================
  // BACKWARD COMPATIBILITY WRAPPERS
  // ============================================================================

  /**
   * Legacy hybridSearch method - wrapper around new search()
   * 
   * @deprecated Use search() instead. Kept for backward compatibility.
   */
  async hybridSearch(
    query: string | Token[],
    options: SearchOptions = {}
  ): Promise<any> {
    const response = await this.search(query, options);

    // Map SearchResponse to legacy format
    return {
      ok: response.ok,
      results: response.results,
      refinements: response.refinements || null,
      nextCursor: response.nextCursor,
      searchMode: response.searchMode,
    };
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private getEdgeFunctionUrl(functionName: string): string {
    return `${this.supabaseUrl}/functions/v1/${functionName}`;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || !session.access_token) {
      throw new Error('Authentication required');
    }
    return {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    };
  }

  // ============================================================================
  // NON-SEARCH METHODS (Preserved from original service)
  // ============================================================================

  /**
   * Ingest content via Edge Function
   */
  async ingest(payload: any): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const url = this.getEdgeFunctionUrl('deepsearch-ingest');

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to ingest content');
      }

      return await response.json();
    } catch (error) {
      const safeError = error instanceof Error ? error.message : 'Failed to process request';
      if (import.meta.env.DEV) {
        console.error('DeepSearch ingest error:', error);
      }
      throw new Error(safeError);
    }
  }

  /**
   * Safe insert item with fallback logic
   * 
   * @deprecated Use ingestionService.captureItem() instead for new code.
   * This method does NOT track items in public.ingestion_events.
   * 
   * For new item creation, use:
   *   import ingestionService from '../services/ingestionService';
   *   await ingestionService.captureItem(userId, itemData, meta);
   */
  async safeInsertItem(item: any, userId: string): Promise<any> {
    try {
      // Validate URL - reject pending:// URLs
      if (item.url && item.url.startsWith('pending://')) {
        throw new Error('pending:// URLs are not allowed. Upload file first, then provide real public URL.');
      }

      // Validate URL format if provided
      if (item.url) {
        try {
          new URL(item.url);
        } catch (urlError) {
          throw new Error(`Invalid URL format: ${item.url}`);
        }
      }

      // Try deepsearch.items first (primary)
      try {
        const { data, error } = await supabase
          .schema('deepsearch')
          .from('items')
          .insert([item])
          .select()
          .single();

        if (!error && data) {
          return { data, destination: 'deepsearch.items' };
        }
        throw error;
      } catch (deepsearchError: any) {
        // Check if it's a schema/table not found error
        const isSchemaError =
          deepsearchError?.code === 'PGRST116' ||
          deepsearchError?.code === 'PGRST205' ||
          deepsearchError?.code === 'PGRST301' ||
          deepsearchError?.message?.includes('schema cache') ||
          deepsearchError?.message?.includes('does not exist');

        if (!isSchemaError) {
          throw deepsearchError;
        }
      }

      // Fallback to public.items
      const { data, error } = await supabase.from('items').insert([item]).select().single();

      if (error) {
        throw error;
      }

      return { data, destination: 'public.items' };
    } catch (error) {
      console.error('safeInsertItem error:', error);
      throw error;
    }
  }

  /**
   * Re-embed single item
   */
  /**
   * Re-embed a single item
   * Note: This method requires the queue_item_for_reembedding RPC to be exposed in the public schema.
   * If not available, it will log a warning and skip the operation.
   */
  async reEmbed(itemId: string): Promise<any> {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        console.warn('[DeepSearch] reEmbed requires authentication. Skipping.');
        return { ok: false, reason: 'Authentication required' };
      }

      // Try calling the public schema wrapper (not the deepsearch schema directly)
      const { error } = await supabase.rpc('queue_item_for_reembedding', {
        p_item_id: itemId,
      });

      if (error) {
        // If the RPC doesn't exist, log a warning instead of throwing
        if (error.message?.includes('Could not find the function') || error.message?.includes('does not exist')) {
          console.warn(
            '[DeepSearch] reEmbed RPC not available in this environment. Skipping re-embed call.',
            { itemId }
          );
          return { ok: false, reason: 'RPC not configured', itemId };
        }
        throw new Error(error.message || 'Failed to queue re-embedding');
      }

      return { ok: true, item_id: itemId };
    } catch (error) {
      console.error('[DeepSearch] Re-embedding error:', error);
      return { ok: false, reason: error instanceof Error ? error.message : 'Unknown error', itemId };
    }
  }

  /**
   * Re-embed all items for the current user
   * Note: This method requires the queue_item_for_reembedding RPC to be exposed in the public schema.
   * If not available, it will log a warning and skip the operation.
   */
  async reEmbedAll(): Promise<any> {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        console.warn('[DeepSearch] reEmbedAll requires authentication. Skipping.');
        return { ok: false, reason: 'Authentication required' };
      }

      const { data: items, error: fetchError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('id')
        .eq('user_id', session.session.user.id);

      if (fetchError) {
        console.error('[DeepSearch] Failed to fetch items for re-embedding:', fetchError);
        return { ok: false, reason: fetchError.message };
      }

      if (!items || items.length === 0) {
        return { ok: true, processed: 0, message: 'No items to re-embed' };
      }

      console.log(`[DeepSearch] Starting re-embed for ${items.length} items...`);

      let processed = 0;
      let errors = 0;
      let skipped = 0;

      for (const item of items) {
        const result = await this.reEmbed(item.id);
        if (result.ok) {
          processed++;
        } else if (result.reason === 'RPC not configured') {
          skipped++;
          // Only log once, not for every item
          if (skipped === 1) {
            console.warn('[DeepSearch] Re-embed RPC not configured. Skipping all re-embed operations.');
          }
          break; // Stop trying if RPC isn't available
        } else {
          errors++;
        }
      }

      const message = skipped > 0
        ? `Re-embed skipped: RPC not configured in this environment`
        : `Re-embedded ${processed} items${errors > 0 ? `, ${errors} errors` : ''}`;

      return {
        ok: skipped === 0,
        processed,
        errors,
        skipped,
        total: items.length,
        message,
      };
    } catch (error) {
      console.error('[DeepSearch] Re-embed all error:', error);
      return { ok: false, reason: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Re-embed all image items with updated color metadata
   * This fixes the embedding pipeline to include primary_color, color_mood, and all auto_tags
   * for better color-based search (e.g., "red shirt", "red")
   */
  async reEmbedAllWithColorFix(): Promise<any> {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        console.warn('[DeepSearch] reEmbedAllWithColorFix requires authentication. Skipping.');
        return { ok: false, reason: 'Authentication required' };
      }

      console.log('[DeepSearch] 🎨 Starting color fix re-embedding for image items...');

      // Fetch all image/visual items
      const { data: items, error: fetchError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('id, source_type')
        .eq('user_id', session.session.user.id)
        .in('source_type', ['image', 'video', 'screenshot', 'reel', 'instagram', 'youtube', 'photo']);

      if (fetchError) {
        console.error('[DeepSearch] Failed to fetch image items:', fetchError);
        return { ok: false, reason: fetchError.message };
      }

      if (!items || items.length === 0) {
        return { ok: true, processed: 0, message: 'No image items to re-embed' };
      }

      console.log(`[DeepSearch] Found ${items.length} visual items to re-embed with color metadata`);

      let processed = 0;
      let errors = 0;
      let skipped = 0;

      for (const item of items) {
        const result = await this.reEmbed(item.id);
        if (result.ok) {
          processed++;
          if (processed % 10 === 0) {
            console.log(`[DeepSearch] Progress: ${processed}/${items.length} items re-embedded...`);
          }
        } else if (result.reason === 'RPC not configured') {
          skipped++;
          if (skipped === 1) {
            console.warn('[DeepSearch] Re-embed RPC not configured. Use scripts/reembed_all_items_full.ts instead.');
          }
          break;
        } else {
          errors++;
        }
      }

      const message = skipped > 0
        ? `Re-embed skipped: RPC not configured. Run: npx tsx scripts/reembed_all_items_full.ts`
        : `✅ Re-embedded ${processed} visual items with color metadata${errors > 0 ? `, ${errors} errors` : ''}`;

      console.log(`[DeepSearch] ${message}`);

      return {
        ok: skipped === 0,
        processed,
        errors,
        skipped,
        total: items.length,
        message,
      };
    } catch (error) {
      console.error('[DeepSearch] Color fix re-embed error:', error);
      return { ok: false, reason: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Reindex item
   */
  async reindex(itemId: string): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const url = this.getEdgeFunctionUrl('deepsearch-reindex');

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ item_id: itemId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Reindex failed');
      }

      return await response.json();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Reindex error:', error);
      }
      throw error;
    }
  }

  /**
   * Health check
   */
  async health(): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const url = this.getEdgeFunctionUrl('deepsearch-health');

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Health check error:', error);
      }
      throw error;
    }
  }

  /**
   * Get all items for user
   */
  /**
   * Get items for a specific user
   * @param userId - User UUID string (required)
   * @param options - Optional filters and pagination
   */
  async getItems(userId: string, options?: { limit?: number; offset?: number }): Promise<any[]> {
    try {
      // Validate userId is a proper UUID string
      if (!userId || typeof userId !== 'string') {
        console.error('[DeepSearch] getItems called with invalid userId:', userId);
        throw new Error('getItems requires a valid userId string (UUID)');
      }

      // Basic UUID format validation
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(userId)) {
        console.error('[DeepSearch] getItems called with invalid UUID format:', userId);
        throw new Error(`Invalid UUID format: ${userId}`);
      }

      let query = supabase
        .schema('deepsearch')
        .from('items')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('[DeepSearch] Error fetching items:', error);
      return [];
    }
  }

  /**
   * Search items with fallback logic
   * @deprecated Use search() method instead
   */
  async searchItems(userId: string, query: string, options: any = {}): Promise<any[]> {
    try {
      const sources = [
        {
          schema: 'deepsearch',
          table: 'items',
          select:
            'id, title, description, url, source_type, auto_tags, primary_tags, extracted_text, primary_color, color_mood, color_palette, meta, created_at',
        },
        {
          schema: null,
          table: 'instagram_content',
          select:
            'id, title, description, url, content_type as source_type, ai_analysis, analysis_data, thumbnail_url, created_at',
        },
      ];

      for (const source of sources) {
        try {
          const escapedQuery = query.replace(/%/g, '\\%').replace(/_/g, '\\_');

          let queryBuilder = source.schema 
            ? supabase.schema(source.schema)
            : supabase.schema('public');

          const { data, error } = await queryBuilder
            .from(source.table)
            .select(source.select)
            .eq('user_id', userId)
            .or(`title.ilike.%${escapedQuery}%,description.ilike.%${escapedQuery}%`)
            .order('created_at', { ascending: false })
            .limit(options.limit || 50);

          if (error) {
            if (
              error.code === 'PGRST116' ||
              error.code === 'PGRST205' ||
              error.code === 'PGRST301' ||
              error.message?.includes('relation') ||
              error.message?.includes('does not exist')
            ) {
              continue;
            }
            throw error;
          }

          if (data && data.length > 0) {
            return data.map(item => ({
              id: item.id,
              title: item.title || 'Untitled',
              description: item.description || '',
              url: item.url,
              source_type: item.source_type || 'unknown',
              tags: item.auto_tags?.topics || item.ai_analysis?.topics || item.analysis_data?.topics || [],
              type: item.source_type || 'unknown',
              auto_tags: item.auto_tags || {},
              ai_analysis: item.ai_analysis || null,
              analysis_data: item.analysis_data || null,
              thumbnail_url: item.thumbnail_url || null,
              meta: item.meta || {},
              created_at: item.created_at,
            }));
          }

          return data || [];
        } catch (tableError) {
          continue;
        }
      }

      return [];
    } catch (error) {
      console.error('Error in searchItems:', error);
      return [];
    }
  }

  /**
   * Get items by tag
   */
  async getByTag(userId: string, tag: string, options: any = {}): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('*')
        .eq('user_id', userId)
        .contains('auto_tags', { topics: [tag] })
        .order('created_at', { ascending: false })
        .limit(options.limit || 50);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching items by tag:', error);
      return [];
    }
  }

  /**
   * Get recent uploads
   */
  async getRecentUploads(userId: string, limit: number = 10): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching recent uploads:', error);
      return [];
    }
  }

  /**
   * Get query history
   */
  async getQueries(userId: string, limit: number = 10): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .schema('deepsearch')
        .from('queries')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching queries:', error);
      return [];
    }
  }

  /**
   * Debug embedding test
   */
  async debugEmbeddingTest(): Promise<any> {
    console.log('Debug embedding test - preserved for debugging');
    return { ok: true, message: 'Debug method preserved' };
  }
}

// Export singleton instance
const deepsearchService = new DeepSearchService();
export default deepsearchService;
